import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, CSSProperties, FC } from 'react';
import { Dropdown, Tooltip } from 'antd';

import DownSvg from 'assets/svg/Down';
import { colorConstants } from 'config/colorConstants';
import { DATE_PICKER_MAX_WEEKDAY_SLOTS, DATE_PICKER_MONTH_LABELS, DATE_PICKER_WEEKDAY_INDEX } from 'utils/Calender';

import {
    BaseControlTypography,
    DATE_PICKER_DROPDOWN_OVERLAY_STYLE,
    DATE_PICKER_PANEL_SHADOW,
    DATE_PICKER_RANGE_SEPARATOR,
    buildYearOptions,
    formatDatePickerRangeValue,
    formatDatePickerValue,
    formatUnknownDatePickerValue,
    getDatePickerDayCellStyle,
    getWeekdayDatesInMonth,
    isDateInInclusiveRange,
    isSameCalendarDate,
    padTwoDigitDatePart,
    parseDatePickerRangeValue,
    resolveTooltipText,
    useOutsideDismiss,
    type GenericDatePickerControlProps,
    type WeekdayDatePickerPanelProps,
} from './formControl';

const DATE_PICKER_RANGE_PLACEHOLDER = `DD/MM/YY${DATE_PICKER_RANGE_SEPARATOR}DD/MM/YY`;

const getDatePickerYearBounds = (currentYear: number, minYear?: number, maxYear?: number) => ({
    minYear: minYear ?? currentYear - 5,
    maxYear: maxYear ?? currentYear + 5,
});

const getDatePickerDisplayColor = (isHighlighted: boolean, hasValue: boolean) =>
    isHighlighted || hasValue ? colorConstants.blackTitle : colorConstants.tableMutedText;

const getDatePickerTriggerLabel = (
    isOpen: boolean,
    draftStart: Date | null,
    draftEnd: Date | null,
    displayText: string,
) => {
    if (!isOpen || !draftStart || draftEnd) return displayText;
    return `${formatDatePickerValue(draftStart)}${DATE_PICKER_RANGE_SEPARATOR}DD/MM/YY`;
};

const shouldShowDatePickerTooltip = (showTooltip: boolean, isOpen: boolean) => showTooltip && !isOpen;

const isStartingNewDateRange = (draftStart: Date | null, draftEnd: Date | null) =>
    !draftStart || Boolean(draftEnd);

const getInitialVisibleYear = (start: Date | null, fallbackYear: number) =>
    start?.getFullYear() ?? fallbackYear;

const getOrderedDateRange = (date: Date, draftStart: Date): [Date, Date] =>
    date.getTime() < draftStart.getTime() ? [date, draftStart] : [draftStart, date];

type DatePickerTriggerProps = {
    disabled: boolean;
    className?: string;
    style?: CSSProperties;
    isHighlighted: boolean;
    isOpen: boolean;
    displayColor: string;
    label: string;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const DatePickerTrigger = forwardRef<HTMLButtonElement, DatePickerTriggerProps>(
    (
        {
            disabled,
            className,
            style,
            isHighlighted,
            isOpen,
            displayColor,
            label,
            onMouseEnter,
            onMouseLeave,
            onClick,
            ...rest
        },
        ref,
    ) => (
        <button
            {...rest}
            ref={ref}
            type="button"
            disabled={disabled}
            className={`box-border flex h-8 w-full min-w-0 max-w-full items-center justify-between overflow-hidden rounded-[4px] border border-solid px-2 ${
                disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            } ${className ?? ''}`.trim()}
            style={{
                borderColor: isHighlighted ? colorConstants.tableCellBorder : 'transparent',
                background: colorConstants.white,
                color: displayColor,
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '22px',
                ...style,
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={(event) => {
                event.stopPropagation();
                onClick?.(event);
            }}
        >
            <span className="min-w-0 truncate text-left">{label}</span>
            <span className="ml-2 flex size-4 shrink-0 items-center justify-center">
                <span className={`transition-transform ${isOpen ? 'rotate-180' : ''}`.trim()}>
                    <DownSvg dropDownStroke={colorConstants.toolbarLabelGrey} />
                </span>
            </span>
        </button>
    ),
);

type WeekdayDateCellProps = {
    date: Date;
    selectedStart: Date | null;
    selectedEnd: Date | null;
    onSelectDate: (date: Date) => void;
};

const WeekdayDateCell = ({ date, selectedStart, selectedEnd, onSelectDate }: WeekdayDateCellProps) => {
    const isEndpoint = isSameCalendarDate(selectedStart, date) || isSameCalendarDate(selectedEnd, date);
    const isInRange = isDateInInclusiveRange(date, selectedStart, selectedEnd);

    return (
        <div className="flex w-9 items-start justify-center px-[6px] py-[3px]">
            <button
                type="button"
                className="flex size-6 min-w-6 items-center justify-center rounded-[6px] px-[3px] text-center text-[14px] font-normal leading-[22px]"
                style={getDatePickerDayCellStyle(isEndpoint, isInRange)}
                onClick={() => onSelectDate(date)}
            >
                {padTwoDigitDatePart(date.getDate())}
            </button>
        </div>
    );
};

const WeekdayDatePickerPanel = ({
    selectedStart,
    selectedEnd,
    visibleYear,
    yearOptions,
    weekdayIndex,
    onYearChange,
    onSelectDate,
}: WeekdayDatePickerPanelProps) => {
    const [isYearMenuOpen, setIsYearMenuOpen] = useState(false);
    const yearMenuRef = useRef<HTMLDivElement | null>(null);

    useOutsideDismiss(yearMenuRef, isYearMenuOpen, () => setIsYearMenuOpen(false));

    const handleYearSelect = (year: number) => {
        onYearChange(year);
        setIsYearMenuOpen(false);
    };

    const renderMonthColumn = (monthIndexes: number[]) => (
        <div className="flex flex-col items-center px-[18px] py-2">
            {monthIndexes.map((monthIndex) => {
                const weekdayDates = getWeekdayDatesInMonth(visibleYear, monthIndex, weekdayIndex);

                return (
                    <div key={DATE_PICKER_MONTH_LABELS[monthIndex]} className="flex items-start">
                        <div className="flex w-9 shrink-0 items-start justify-center px-[6px] py-[3px]">
                            <span
                                className="flex h-6 min-w-6 items-center justify-center px-[3px] text-center text-[14px] font-medium leading-[22px]"
                                style={{ color: colorConstants.blackTitle }}
                            >
                                {DATE_PICKER_MONTH_LABELS[monthIndex]}
                            </span>
                        </div>
                        {Array.from({ length: DATE_PICKER_MAX_WEEKDAY_SLOTS }, (_, slotIndex) => {
                            const day = weekdayDates[slotIndex];
                            if (!day) {
                                return <div key={`empty-${monthIndex}-${slotIndex}`} className="h-[30px] w-9" />;
                            }

                            const date = new Date(visibleYear, monthIndex, day);
                            return (
                                <WeekdayDateCell
                                    key={`${visibleYear}-${monthIndex}-${day}`}
                                    date={date}
                                    selectedStart={selectedStart}
                                    selectedEnd={selectedEnd}
                                    onSelectDate={onSelectDate}
                                />
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="flex flex-col items-start rounded-[6px] bg-white pb-2" style={{ boxShadow: DATE_PICKER_PANEL_SHADOW }}>
            <div
                className="flex w-full items-center px-2 py-3"
                style={{ borderBottom: `1px solid ${colorConstants.blackTransparent05}` }}
            >
                <div ref={yearMenuRef} className="relative">
                    <button
                        type="button"
                        className="flex h-6 w-20 items-center gap-1 rounded-[6px] border px-2"
                        style={{
                            borderColor: colorConstants.toolbarBorderGrey,
                            background: colorConstants.white,
                            color: colorConstants.blackTransparent88,
                            ...BaseControlTypography,
                        }}
                        onClick={() => setIsYearMenuOpen((previous) => !previous)}
                    >
                        <span className="min-w-0 flex-1 truncate text-left">{visibleYear}</span>
                        <span className="flex size-4 shrink-0 items-center justify-center">
                            <span className={`transition-transform ${isYearMenuOpen ? 'rotate-180' : ''}`.trim()}>
                                <DownSvg dropDownStroke={colorConstants.toolbarLabelGrey} />
                            </span>
                        </span>
                    </button>
                    {isYearMenuOpen ? (
                        <div
                            className="absolute left-0 top-[calc(100%+4px)] z-10 max-h-40 w-20 overflow-auto rounded-md border bg-white py-1"
                            style={{
                                borderColor: colorConstants.toolbarBorderGrey,
                                boxShadow: DATE_PICKER_PANEL_SHADOW,
                            }}
                        >
                            {yearOptions.map((year) => (
                                <button
                                    key={year}
                                    type="button"
                                    className="flex h-7 w-full items-center px-2 text-left text-[14px]"
                                    style={{
                                        color: colorConstants.blackTitle,
                                        background: year === visibleYear ? colorConstants.blackTransparent04 : 'transparent',
                                    }}
                                    onClick={() => handleYearSelect(year)}
                                >
                                    {year}
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="flex items-start">
                {renderMonthColumn([0, 1, 2, 3, 4, 5])}
                <div style={{ width: 1, alignSelf: 'stretch', background: colorConstants.blackTransparent12 }} />
                {renderMonthColumn([6, 7, 8, 9, 10, 11])}
            </div>
        </div>
    );
};

export const GenericDatePickerControl: FC<GenericDatePickerControlProps> = ({
    value,
    onChange,
    placeholder = DATE_PICKER_RANGE_PLACEHOLDER,
    disabled = false,
    className,
    style,
    startWeekday = 'friday',
    minYear,
    maxYear,
    showTooltip = false,
    tooltipText,
    staticTooltipText,
}) => {
    const currentYear = new Date().getFullYear();
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const committedRange = useMemo(() => parseDatePickerRangeValue(value), [value]);
    const [draftStart, setDraftStart] = useState<Date | null>(committedRange.start);
    const [draftEnd, setDraftEnd] = useState<Date | null>(committedRange.end);
    const [visibleYear, setVisibleYear] = useState(getInitialVisibleYear(committedRange.start, currentYear));

    const { minYear: resolvedMinYear, maxYear: resolvedMaxYear } = getDatePickerYearBounds(
        currentYear,
        minYear,
        maxYear,
    );
    const yearOptions = useMemo(
        () => buildYearOptions(resolvedMinYear, resolvedMaxYear),
        [resolvedMaxYear, resolvedMinYear],
    );
    const formattedValue = formatUnknownDatePickerValue(value);
    const hasValue = Boolean(formattedValue);
    const displayText = hasValue ? formattedValue : placeholder;
    const resolvedTooltipText = resolveTooltipText(staticTooltipText, tooltipText, displayText);
    const isHighlighted = isOpen || isHovered;
    const displayColor = getDatePickerDisplayColor(isHighlighted, hasValue);
    const triggerLabel = getDatePickerTriggerLabel(isOpen, draftStart, draftEnd, displayText);

    useEffect(() => {
        if (!isOpen) {
            setDraftStart(committedRange.start);
            setDraftEnd(committedRange.end);
        }
        if (committedRange.start) setVisibleYear(committedRange.start.getFullYear());
    }, [committedRange.end, committedRange.start, isOpen]);

    const handleOpenChange = (nextOpen: boolean) => {
        if (disabled) return;
        if (nextOpen) {
            setDraftStart(committedRange.start);
            setDraftEnd(committedRange.end);
        }
        setIsOpen(nextOpen);
    };

    const handleSelectDate = (date: Date) => {
        if (isStartingNewDateRange(draftStart, draftEnd)) {
            setDraftStart(date);
            setDraftEnd(null);
            return;
        }

        const [start, end] = getOrderedDateRange(date, draftStart as Date);
        onChange(formatDatePickerRangeValue(start, end));
        setIsOpen(false);
    };

    const trigger = (
        <DatePickerTrigger
            disabled={disabled}
            className={className}
            style={style}
            isHighlighted={isHighlighted}
            isOpen={isOpen}
            displayColor={displayColor}
            label={triggerLabel}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        />
    );

    const dropdown = (
        <Dropdown
            trigger={['click']}
            open={isOpen}
            onOpenChange={handleOpenChange}
            disabled={disabled}
            placement="bottomLeft"
            overlayClassName="date-picker-control-dropdown"
            overlayStyle={DATE_PICKER_DROPDOWN_OVERLAY_STYLE}
            destroyPopupOnHide
            getPopupContainer={() => document.body}
            dropdownRender={() => (
                <div
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <WeekdayDatePickerPanel
                        selectedStart={draftStart}
                        selectedEnd={draftEnd}
                        visibleYear={visibleYear}
                        yearOptions={yearOptions}
                        weekdayIndex={DATE_PICKER_WEEKDAY_INDEX[startWeekday]}
                        onYearChange={setVisibleYear}
                        onSelectDate={handleSelectDate}
                    />
                </div>
            )}
        >
            {trigger}
        </Dropdown>
    );

    if (!shouldShowDatePickerTooltip(showTooltip, isOpen)) return dropdown;

    return (
        <Tooltip title={resolvedTooltipText}>
            <div className="inline-block w-full">{dropdown}</div>
        </Tooltip>
    );
};

export default GenericDatePickerControl;
